import inRange from 'lodash/inRange';

/**
 * @description Returns the distance, in meters, between two LatLngs.
 * @param {{lat: Number, lng: Number}} from Latitude and longitude of the first point
 * @param {{lat: Number, lng: Number}} to Latitude and longitude of the second point
 * @param {Object} loader Google Maps loader object
 * @returns {Number} Distance in meters
 * @example getDistance({lat, lng}, {lat, lng}, loader);
 * @reference https://developers.google.com/maps/documentation/javascript/reference/geometry#spherical.computeDistanceBetween
 */
export const getDistance = async (from, to, loader) => {
  if (!from || !to || !loader) return null;

  const [{ LatLng }, { spherical }] = await Promise.all([
    loader.importLibrary('core'),
    loader.importLibrary('geometry'),
  ]);

  let fromA = new LatLng(from.lat, from.lng);
  let toB = new LatLng(to.lat, to.lng);

  return spherical.computeDistanceBetween(fromA, toB);
};

/**
 * @description Converts a distance from meters to kilometers.
 * @param {Number} distance Distance expressed in meters
 * @returns {String} Distance in km
 */
export const distanceInKm = (distance) => {
  return (distance / 1000).toFixed(1);
};

/**
 * @description Converts a distance from meters to miles.
 * @param {Number} distance Distance expressed in meters
 * @returns {String} Distance in miles
 */
export const distanceInMiles = (distance) => {
  return (distance / 1609.34).toFixed(1);
};

export const getMeasurementSystem = () => {
  const locale = navigator.language || navigator.userLanguage;
  const country = locale.split('-')[1]?.toUpperCase();

  // Countries using the imperial system: United States, Liberia, Myanmar
  const imperialCountries = new Set(['US', 'LR', 'MM']);

  return imperialCountries.has(country) ? 'imperial' : 'metric';
};

/**
 * @description Converts a distance from meters to the selected unit.
 * @param {Number} distance Distance expressed in meters
 * @param {'metric'|'imperial'} system Unit Measurement system, default is 'metric'
 * @returns {String} Distance in the selected unit
 */
export const getDistanceUnit = (distance, system) => {
  if (!system) system = getMeasurementSystem();
  if (system === 'metric') return distanceInKm(distance) + ' km';
  if (system === 'imperial') return distanceInMiles(distance) + ' mi';
  return distance;
};

/**
 * @description Sorts stores by distance (ascending)
 * @param {Array.<object>} stores - Array of store objects
 * @returns {Array.<object>} Sorted stores array
 */
export const sortStoresByDistance = (stores) => {
  if (!stores || !Array.isArray(stores)) {
    return [];
  }

  // Create a copy to avoid mutating the original array
  const sorted = [...stores].sort((a, b) => {
    // Stores with distance come first
    const aHasDistance = a.distance !== null && a.distance !== undefined && Number.isFinite(a.distance);
    const bHasDistance = b.distance !== null && b.distance !== undefined && Number.isFinite(b.distance);

    if (aHasDistance && !bHasDistance) return -1;
    if (!aHasDistance && bHasDistance) return 1;
    if (aHasDistance && bHasDistance) {
      return a.distance - b.distance;
    }

    // If neither has distance, maintain original order (alphabetically by name)
    return (a.name || '').localeCompare(b.name || '');
  });

  return sorted;
};

/**
 * @description Detect if coordinates are valid.
 *
 * Latitude must be a value between -90 and 90.
 * Longitude must be a value between -180 and 180.
 * @param {{lat: number, lng: number}} coordinates - Coordinates
 * @returns {Boolean} true || false
 */
export const isValidCoordinates = (coordinates) => {
  const { lat, lng } = coordinates || {};
  return lat && lng && inRange(lat, -90, 90) && inRange(lng, -180, 180);
};

/**
 * @description Checks if two sets are equal.
 * @param {Set} a - First set to compare.
 * @param {Set} b - Second set to compare.
 * @returns {Boolean} true if sets are equal, false otherwise.
 * @example setsAreEqual(new Set([1, 2, 3]), new Set([1, 2, 3])); // true
 * @example setsAreEqual(new Set([1, 2]), new Set([1, 2, 3])); // false
 */
export const setsAreEqual = (a, b) => {
  if (a.size !== b.size) return false;
  for (const val of a) if (!b.has(val)) return false;
  return true;
};

/**
 * @description Parses the opening hours object from Google Places API and returns a more readable format.
 * @param {Object} store - Store object containing opening_hours data.
 * @param {String} closedStr - String to use for closed days (default: 'Closed').
 * @returns {Object|null} An object with formatted opening hours or null if no opening_hours data is available.
 * @example
 * const store = {
 *   opening_hours: {
 *     monday: {
 *       label: "Monday",
 *       morning_from: "08:00",
 *       morning_to: "12:00",
 *       closed_morning: false,
 *       afternoon_from: "13:00",
 *       afternoon_to: "17:00",
 *       closed_afternoon: false
 *     },
 *     tuesday: { ... },
 *     ...
 *   }
 * };
 * const hours = getOpeningHours(store, 'Closed');
 * // {
 * //   "0-monday": "Mon: 08:00 - 12:00 / 13:00 - 17:00",
 * //   "1-tuesday": "Tue: Closed",
 * //   ...
 * // }
 */
export const getOpeningHours = (store, closedStr = 'Closed') => {
  if (!store?.opening_hours) return null;

  const openingHours = {};

  Object.entries(store.opening_hours).forEach(([key, day], index) => {
    if (!day) return;

    const dayName = day.label ? day.label.slice(0, 3) : key.slice(0, 3);

    const mf = day.morning_from;
    const mt = day.morning_to;
    const mc = day.closed_morning;

    const af = day.afternoon_from;
    const at = day.afternoon_to;
    const ac = day.closed_afternoon;

    let result = '';

    // Case 1: all day closed (es. 00:00–00:00)
    if (mc && ac) {
      result = `${dayName}: ${closedStr}`;
    }
    // Case 2: continued opening (es. 00:00–23:59)
    else if (!mc && !ac && mf && mt && af && at && mt === '00:00' && af === '00:00') {
      result = `${dayName}: ${mf} - ${at}`;
    }
    // Case 3: both morning and afternoon open
    else if (!mc && !ac && mf && mt && af && at) {
      result = `${dayName}: ${mf} - ${mt} / ${af} - ${at}`;
    }
    // Case 4: only morning
    else if (!mc && mf && mt && (ac || !af || !at)) {
      result = `${dayName}: ${mf} - ${mt} / ${closedStr}`;
    }
    // Case 5: only afternoon
    else if (!ac && af && at && (mc || !mf || !mt)) {
      result = `${dayName}: ${closedStr} / ${af} - ${at}`;
    }
    // fallback
    else {
      result = `${dayName}: ${closedStr}`;
    }

    // Keep the index in the key to maintain order
    openingHours[`${index}-${key}`] = result;
  });

  return openingHours;
};

/**
 * @description Determines if a store is currently open based on a given opening hours string.
 * @param {String} openingString - A string representing the opening hours for a specific day (e.g., "Wed: 08:00 - 12:00 / 13:00 - 17:00").
 * @returns {Boolean} true if the store is currently open, false otherwise.
 * @example isStoreOpenNow("Wed: 08:00 - 12:00 / 13:00 - 17:00"); // returns true or false based on current time
 */
export const isStoreOpenNow = (openingString) => {
  // E.g. input: "Wed: 08:00 - 12:00 / 13:00 - 17:00"
  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  // Take the part after the colon
  const timesPart = openingString.split(':').slice(1).join(':').trim();
  // Split by slot (morning/afternoon)
  const slots = timesPart.split('/').map((s) => s.trim());

  for (const slot of slots) {
    // If the slot indicates closed in any language, skip it
    if (/^[\p{L}\s]+$/u.test(slot)) {
      continue; // slot not valid, skip
    }

    // otherwise "HH:MM - HH:MM"
    const [start, end] = slot.split('-').map((s) => s.trim());
    if (!start || !end) continue;

    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);

    const startMinutes = sh * 60 + sm;
    const endMinutes = eh * 60 + em;

    if (currentMinutes >= startMinutes && currentMinutes <= endMinutes) {
      return true; // within a valid slot
    }
  }

  return false; // not within any valid slot
};
